import { useRef, useState } from 'react'
import { format, parseISO, subDays } from 'date-fns'
import { CalendarDays, FileSpreadsheet, Upload } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { PromoteJoistDialog } from '@/components/PromoteJoistDialog'
import { useImportJoistScan, useScanJoistExport } from '@/hooks/useJoistLeads'
import type {
  JoistDuplicateLink,
  JoistImportResponse,
  JoistScanResponse,
  JoistTimeWindow,
} from '@/types/api'

const timeWindowOptions: { value: JoistTimeWindow; label: string }[] = [
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'full', label: 'Full file' },
  { value: 'custom', label: 'Custom range' },
]

/** phone_other -> "Phone other", last_contacted_at -> "Last contacted at" */
function formatFieldName(field: string): string {
  const spaced = field.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

export function JoistLeadsPage() {
  const estimatesRef = useRef<HTMLInputElement>(null)
  const clientsRef = useRef<HTMLInputElement>(null)

  const [estimatesFile, setEstimatesFile] = useState<File | null>(null)
  const [clientsFile, setClientsFile] = useState<File | null>(null)
  const [timeWindow, setTimeWindow] = useState<JoistTimeWindow>('yesterday')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [scan, setScan] = useState<JoistScanResponse | null>(null)
  const [importResult, setImportResult] = useState<JoistImportResponse | null>(null)
  const [promoteOpen, setPromoteOpen] = useState(false)
  const [duplicates, setDuplicates] = useState<JoistDuplicateLink[]>([])

  const scanMutation = useScanJoistExport()
  const importMutation = useImportJoistScan()

  const yesterday = format(subDays(new Date(), 1), 'yyyy-MM-dd')
  const canScan =
    Boolean(estimatesFile) &&
    Boolean(clientsFile) &&
    (timeWindow !== 'custom' || (Boolean(startDate) && Boolean(endDate))) &&
    !scanMutation.isPending

  function handleScan() {
    if (!estimatesFile || !clientsFile) return
    setImportResult(null)
    setDuplicates([])
    scanMutation.mutate(
      {
        estimatesFile,
        clientsFile,
        timeWindow,
        startDate: timeWindow === 'custom' ? startDate : undefined,
        endDate: timeWindow === 'custom' ? endDate : undefined,
      },
      { onSuccess: setScan }
    )
  }

  function handleImport() {
    if (!scan) return
    importMutation.mutate(scan.scan_id, {
      onSuccess: (result) => {
        setImportResult(result)
        // Reflect the new state so the Import button cannot be pressed twice
        setScan((prev) =>
          prev
            ? {
                ...prev,
                status: 'imported',
                inserted_count: result.inserted,
                skipped_count: result.skipped_existing,
              }
            : prev
        )
        // Only ask about promotion when something actually landed
        if (result.inserted > 0) setPromoteOpen(true)
      },
    })
  }

  function handleCancel() {
    // Clearing React state is not enough - the File stays attached to the DOM node
    // and the next scan would silently re-send it.
    if (estimatesRef.current) estimatesRef.current.value = ''
    if (clientsRef.current) clientsRef.current.value = ''

    setEstimatesFile(null)
    setClientsFile(null)
    setTimeWindow('yesterday')
    setStartDate('')
    setEndDate('')
    setScan(null)
    setImportResult(null)
    setPromoteOpen(false)
    setDuplicates([])
  }

  return (
    <div className="space-y-3">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Sync LTS with Joist</h1>
        <p className="max-w-4xl text-muted-foreground">
          The leads that came by other means and already had an in-home appointment that
          generated an estimate are scanned from two files, so that LTS holds the full
          information about those leads and their projects.
        </p>
      </div>

      <Card>
        <CardContent className="space-y-6 pt-6">
          {/* File pickers */}
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="estimates-file" className="flex items-center gap-2">
                <FileSpreadsheet className="h-4 w-4" />
                Estimates
              </Label>
              <Input
                id="estimates-file"
                ref={estimatesRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setEstimatesFile(e.target.files?.[0] ?? null)}
              />
              <p className="text-xs text-muted-foreground">Joist estimates export (.csv)</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="clients-file" className="flex items-center gap-2">
                <FileSpreadsheet className="h-4 w-4" />
                Clients
              </Label>
              <Input
                id="clients-file"
                ref={clientsRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setClientsFile(e.target.files?.[0] ?? null)}
              />
              <p className="text-xs text-muted-foreground">Joist clients export (.csv)</p>
            </div>
          </div>

          <div className="border-t" />

          {/* Time window */}
          <fieldset className="space-y-3">
            <legend className="mb-3 flex items-center gap-2 text-sm font-medium">
              <CalendarDays className="h-4 w-4" />
              Time window
            </legend>

            <div className="flex flex-wrap items-center gap-6">
              {timeWindowOptions.map((opt) => (
                <label
                  key={opt.value}
                  htmlFor={`window-${opt.value}`}
                  className="flex cursor-pointer items-center gap-2 text-sm"
                >
                  <input
                    id={`window-${opt.value}`}
                    type="radio"
                    name="time_window"
                    value={opt.value}
                    checked={timeWindow === opt.value}
                    onChange={() => setTimeWindow(opt.value)}
                    className="h-4 w-4 accent-primary"
                  />
                  {opt.label}
                  {opt.value === 'yesterday' && (
                    <span className="text-muted-foreground">({yesterday})</span>
                  )}
                </label>
              ))}
            </div>

            {timeWindow === 'custom' && (
              <div className="flex flex-wrap items-end gap-4 pt-2">
                <div className="space-y-2">
                  <Label htmlFor="start-date">Start date</Label>
                  <Input
                    id="start-date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-[180px]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end-date">End date</Label>
                  <Input
                    id="end-date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-[180px]"
                  />
                </div>
                <p className="pb-2 text-sm text-muted-foreground">
                  Both the start and the end date are included in the scan.
                </p>
              </div>
            )}
          </fieldset>

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            <Button onClick={handleScan} disabled={!canScan}>
              <Upload className="mr-2 h-4 w-4" />
              {scanMutation.isPending ? 'Scanning...' : 'Scan'}
            </Button>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>

          {scanMutation.isPending && (
            <div className="flex justify-center py-6">
              <Spinner size="lg" />
            </div>
          )}

          {/* Preview */}
          {scan && !scanMutation.isPending && (
            <div className="space-y-3 border-t pt-6" data-testid="scan-preview">
              <div>
                <p className="text-sm font-medium">
                  {scan.importable_rows === 1
                    ? '1 lead to load'
                    : `${scan.importable_rows} leads to load`}
                </p>
                {(scan.flagged_rows > 0 || scan.unmatched_rows > 0) && (
                  <p className="text-xs text-muted-foreground">
                    {scan.unmatched_rows > 0 && (
                      <span>
                        {scan.unmatched_rows} not found in the clients file
                        {scan.flagged_rows > 0 && ' · '}
                      </span>
                    )}
                    {scan.flagged_rows > 0 && (
                      <span>{scan.flagged_rows} without an email or phone</span>
                    )}
                  </p>
                )}
              </div>

              {scan.sample.length > 0 && (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Client Name</TableHead>
                        <TableHead>Email Address</TableHead>
                        <TableHead>Phone (mobile)</TableHead>
                        <TableHead>Address</TableHead>
                        <TableHead>Date Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {scan.sample.map((row, i) => (
                        <TableRow key={`${row.client_name}-${i}`}>
                          <TableCell className="font-medium">{row.client_name}</TableCell>
                          <TableCell>{row.email || '-'}</TableCell>
                          <TableCell>{row.phone || '-'}</TableCell>
                          <TableCell>{row.address || '-'}</TableCell>
                          <TableCell>
                            {format(parseISO(row.date_created), 'yyyy-MM-dd')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {scan.importable_rows > 0 && (
                <Button
                  onClick={handleImport}
                  disabled={importMutation.isPending || scan.status === 'imported'}
                >
                  {importMutation.isPending
                    ? 'Importing...'
                    : scan.status === 'imported'
                      ? 'Imported'
                      : 'Import'}
                </Button>
              )}

              {importResult && (
                <p className="text-sm text-success" data-testid="import-result">
                  {importResult.message}
                </p>
              )}

              {/* Which leads were enriched rather than created, and why */}
              {duplicates.length > 0 && (
                <div className="space-y-2 pt-2" data-testid="duplicate-report">
                  <p className="text-sm font-medium">
                    {duplicates.length === 1
                      ? '1 lead already existed and was updated'
                      : `${duplicates.length} leads already existed and were updated`}
                  </p>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Client</TableHead>
                          <TableHead>Matched on</TableHead>
                          <TableHead>Updated fields</TableHead>
                          <TableHead>Lead</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {duplicates.map((d) => (
                          <TableRow key={d.joist_lead_id}>
                            <TableCell className="font-medium">{d.client_name}</TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {(d.matched_on ?? []).map((m) => (
                                  <Badge key={m} variant="secondary">
                                    {m}
                                  </Badge>
                                ))}
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {d.filled_fields?.length
                                ? d.filled_fields.map(formatFieldName).join(', ')
                                : '—'}
                            </TableCell>
                            <TableCell>
                              {/* Plain anchor, not <Link>: this page is rendered in tests
                                  without a Router, and a router hook would throw */}
                              <a
                                href={`/leads/${d.lead_id}`}
                                className="text-primary underline-offset-4 hover:underline"
                              >
                                View
                              </a>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <PromoteJoistDialog
        open={promoteOpen}
        onOpenChange={setPromoteOpen}
        scanId={scan?.scan_id ?? null}
        onDuplicates={(links) => setDuplicates((prev) => [...prev, ...links])}
      />
    </div>
  )
}
